import { useState } from 'react';
import { useCart } from '@/context/CartContext';

interface BlueprintCTAProps {
  id: string;
  title: string;
  includes: string[];
}

const BlueprintCTA = ({ id, title, includes }: BlueprintCTAProps) => {
  const { addToCart } = useCart();
  const [added, setAdded] = useState(false);

  const handleAddToCart = () => {
    addToCart({
      id: `blueprint-${id}`,
      name: `Blueprint: ${title}`,
      price: 14.95,
      type: 'blueprint',
      description: 'Digitale Blueprint — direct per e-mail bezorgd na aankoop.',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className="my-12">
      {/* Vergrendeld preview hint */}
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-gradient-to-t from-[#080015] via-[#080015]/60 to-transparent z-10 rounded-2xl pointer-events-none" />
        <div className="p-6 bg-white/3 border border-violet-500/15 rounded-2xl relative">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-lock-line text-violet-400 text-sm" />
            </div>
            <span className="text-violet-400 text-xs font-semibold uppercase tracking-widest">Premium inhoud</span>
          </div>
          {[1, 2, 3].map(i => (
            <div key={i} className="h-3 bg-white/8 rounded-full mb-2.5" style={{ width: `${100 - i * 10}%` }} />
          ))}
          <div className="h-3 bg-white/8 rounded-full w-3/4 mb-2.5" />
          <div className="h-3 bg-white/8 rounded-full w-1/2" />
        </div>
        <div className="absolute bottom-4 left-0 right-0 z-20 flex justify-center">
          <div className="flex items-center gap-2 px-4 py-2 bg-violet-600/30 backdrop-blur-sm border border-violet-500/40 rounded-full">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-lock-fill text-violet-300 text-xs" />
            </div>
            <span className="text-violet-200 text-xs font-semibold">Volledige inhoud beschikbaar via Blueprint</span>
          </div>
        </div>
      </div>

      {/* Blueprint aankoop kaart */}
      <div className="p-8 bg-gradient-to-br from-violet-900/25 to-fuchsia-900/25 border border-violet-500/30 rounded-3xl">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* Links: info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-600/30">
                <i className="ri-file-download-line text-violet-400 text-base" />
              </div>
              <span className="text-violet-400 text-xs font-semibold uppercase tracking-widest">Digitale Blueprint</span>
            </div>
            <h3 className="text-white font-black text-xl lg:text-2xl mb-3 leading-snug">{title}</h3>
            <p className="text-white/55 text-sm leading-relaxed mb-5">
              De volledige stap-voor-stap gids — direct na aankoop bezorgd via e-mail.
              Geen wachttijd, direct toepasbaar.
            </p>
            <ul className="space-y-2">
              {includes.map(item => (
                <li key={item} className="flex items-start gap-2.5">
                  <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-violet-400 text-sm" />
                  </div>
                  <span className="text-white/70 text-sm leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Rechts: prijs + in wagen */}
          <div className="w-full lg:w-72 shrink-0">
            <div className="p-6 bg-[#0d0025]/80 border border-violet-500/25 rounded-2xl text-center">
              <p className="text-white/40 text-xs uppercase tracking-wide mb-1">Eenmalige aankoop</p>
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <span className="text-4xl font-black text-white">&euro;14</span>
                <span className="text-2xl font-black text-white">,95</span>
              </div>
              <p className="text-white/35 text-xs mb-5">
                <i className="ri-mail-send-line mr-1 text-violet-400" />
                Direct per e-mail bezorgd
              </p>

              <button
                onClick={handleAddToCart}
                className={`w-full py-4 font-bold rounded-xl text-base whitespace-nowrap cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 ${
                  added
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-90'
                }`}
              >
                {added ? (
                  <>
                    <i className="ri-check-line" />
                    Toegevoegd aan wagen!
                  </>
                ) : (
                  <>
                    <i className="ri-shopping-bag-line" />
                    In Wagen — &euro;14,95
                  </>
                )}
              </button>

              <div className="mt-4 space-y-1.5">
                <div className="flex items-center justify-center gap-1.5 text-white/30 text-xs">
                  <i className="ri-shield-check-line text-violet-400 text-xs" />
                  Veilig betalen via Stripe
                </div>
                <div className="flex items-center justify-center gap-1.5 text-white/30 text-xs">
                  <i className="ri-bank-card-line text-violet-400 text-xs" />
                  iDEAL, creditcard &amp; meer
                </div>
                <div className="flex items-center justify-center gap-1.5 text-white/30 text-xs">
                  <i className="ri-file-pdf-line text-violet-400 text-xs" />
                  PDF direct in mailbox
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlueprintCTA;
